import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Anchor, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Controller, Resolver, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import { useCreatePatientMutation } from '@/store/slices/api/patient/patientListApiSlice';
import { useCreateTaskMutation, useListTaskTypesQuery } from '@/store/slices/api/tasksApiSlice';
import { useGetUserSettingsQuery } from '@/store/slices/api/userApiSlice';
import { getMrnLabel } from '@/utils/getMrnLabel';
import { routes } from '@/routes';
import { YellowAlertIcon } from '@/assets/icons/YellowAlertIcon';
import poc from '@/styles/pocScope.module.scss';

import { Organization } from '@/types/organization';
import { CreatePatientDto, PatientSex } from '@/types/patientList';
import CustomModal from '@/ui/Modal';

import { DeviceVendors } from '../analytics/constants';

import ChoiceCards from './ChoiceCards';
import { clinicScope } from './clinicScope';
import ClinicSelect from './ClinicSelect';
import styles from './CreatePatientModal.module.scss';
import { DeviceFields, PatientFields } from './ModalFields';
import {
  asksWhoMonitors,
  clinicMonitoringConfig,
  CREATE_PATIENT_STEPS,
  CreatePatientStep,
  createsEnrollmentTask,
  MonitoredBy,
  rhythmMonitored
} from './monitoringRules';
import StepIndicator from './StepIndicator';

export function clinicRequiresInternalMrn(
  clinic?:
    | (Pick<Organization, 'ehrIntegration'> & {
        parent?: Pick<Organization, 'ehrIntegration'> | null;
      })
    | null
): boolean {
  if (!clinic) return false;
  if (clinic.parent) return clinic.parent.ehrIntegration === true;
  return clinic.ehrIntegration === true;
}

export const CPE_TASK_TYPE_CODE = 'candidate_patient_enrollment';

export interface CreatePatientFormValues {
  /** '' until answered. */
  monitoring: '' | 'remote' | 'in_clinic';
  /** Platform Only accounts only. */
  monitoredBy: '' | MonitoredBy;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  sex: string;
  mrn: string;
  clinicId: string;
  vendor: string;
  type: string;
  serialNumber: string;
  modelName?: string;
  modelNumber?: string;
  implantDate: string;
}

const STEP_LABEL: Record<CreatePatientStep, string> = {
  monitoring: 'Monitoring',
  patient: 'Patient',
  device: 'Device'
};

const STEP_FIELDS: Record<CreatePatientStep, (keyof CreatePatientFormValues)[]> = {
  monitoring: ['clinicId', 'monitoring', 'monitoredBy'],
  patient: ['firstName', 'lastName', 'dob', 'sex', 'mrn'],
  device: ['vendor', 'type', 'serialNumber', 'modelName', 'modelNumber', 'implantDate']
};

const EMPTY_VALUES: CreatePatientFormValues = {
  monitoring: 'remote',
  monitoredBy: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dob: '',
  sex: '',
  mrn: '',
  clinicId: '',
  vendor: '',
  type: '',
  serialNumber: '',
  modelName: '',
  modelNumber: '',
  implantDate: ''
};

const CreatePatientModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [isError, setIsError] = useState(false);
  const [errorType, setErrorType] = useState<'Patient' | 'Device' | false>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorPatientId, setErrorPatientId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  const { user: currentUser } = useAppSelector((state: RootState) => state.auth);
  const isInternalUser = Boolean(currentUser?.role?.includes('internal'));
  const { data: userSettings } = useGetUserSettingsQuery();
  const clinicsRef = useRef(userSettings?.clinics ?? []);
  clinicsRef.current = userSettings?.clinics ?? [];
  // The org fetched for the selected clinic, so MRN uses the account EHR flag rather than
  // whatever the user-settings clinic list happened to include.
  const mrnClinicRef = useRef<Parameters<typeof clinicRequiresInternalMrn>[0]>(null);
  // Active clinics only; the patient list's clinic filter narrows the picker by default.
  const scope = useMemo(
    () => clinicScope(userSettings?.clinics, userSettings?.filteredClinics),
    [userSettings?.clinics, userSettings?.filteredClinics]
  );
  const [useFilter, setUseFilter] = useState(true);
  const shownClinics = scope.filtered.length && useFilter ? scope.filtered : scope.all;
  // The only clinic shown, else the user's own org when it is shown.
  const userOrgId = currentUser?.organizations?.[0]?.id ?? '';
  const defaultClinicId =
    shownClinics.length === 1
      ? shownClinics[0].id
      : shownClinics.some((clinic) => clinic.id === userOrgId)
        ? userOrgId
        : '';

  // Rules are read from the clinic and its parent account.
  const configRef = useRef(clinicMonitoringConfig(null));

  const { control, handleSubmit, watch, reset, trigger, setValue } =
    useForm<CreatePatientFormValues>({
      resolver: yupResolver(
        Yup.object().shape({
          monitoring: Yup.string()
            .oneOf(['remote', 'in_clinic'], 'Choose how this patient will be monitored')
            .required('Choose how this patient will be monitored'),
          monitoredBy: Yup.string().test(
            'who-monitors',
            'Choose who will monitor',
            function (value) {
              if (!asksWhoMonitors(configRef.current, this.parent.monitoring === 'remote'))
                return true;
              return value === 'rhythm' || value === 'clinic';
            }
          ),
          firstName: Yup.string().required('First name is required'),
          middleName: Yup.string(),
          lastName: Yup.string().required('Last name is required'),
          dob: Yup.string().required('Date of birth is required'),
          sex: Yup.string().required('Sex is required'),
          mrn: Yup.string().test('ehr-mrn', 'MRN is required', function (value) {
            const clinic =
              mrnClinicRef.current ??
              clinicsRef.current.find((item) => item.id === this.parent.clinicId);
            if (!clinicRequiresInternalMrn(clinic)) return true;
            return Boolean(value?.trim());
          }),
          clinicId: Yup.string().required('Clinic is required'),
          vendor: Yup.string().required('Manufacturer is required'),
          type: Yup.string().required('Type is required'),
          serialNumber: Yup.string().required('Serial number is required'),
          modelName: Yup.string().when('vendor', {
            is: (val: string) => val?.toLowerCase() === 'biotronik',
            then: (schema) => schema.required('Model name is required for Biotronik'),
            otherwise: (schema) => schema.optional()
          }),
          modelNumber: Yup.string().when('vendor', {
            is: (val: string) => val?.toLowerCase() !== 'biotronik',
            then: (schema) =>
              schema.required('Model number is required unless vendor is Biotronik'),
            otherwise: (schema) => schema.optional()
          }),
          implantDate: Yup.string().required('Implant date is required')
        })
      ) as unknown as Resolver<CreatePatientFormValues>,
      mode: 'onChange',
      defaultValues: { ...EMPTY_VALUES, clinicId: defaultClinicId }
    });

  const [createPatient] = useCreatePatientMutation();
  const [createTask] = useCreateTaskMutation();
  const { data: taskTypes = [] } = useListTaskTypesQuery(undefined, { skip: !isInternalUser });

  const vendor = watch('vendor');
  const clinicId = watch('clinicId');
  const monitoring = watch('monitoring');
  const monitoredBy = watch('monitoredBy');
  const deviceType = watch('type');
  const isRemote = monitoring === 'remote';

  const listedClinic = userSettings?.clinics.find((clinic) => clinic.id === clinicId);
  const { data: clinicData, isFetching: clinicLoading } = useGetOrgByIdQuery(clinicId, {
    skip: !clinicId
  });
  const clinic = clinicData?.organization ?? listedClinic;
  mrnClinicRef.current = clinic ?? null;
  const config = useMemo(() => clinicMonitoringConfig(clinic), [clinic]);
  configRef.current = config;

  const steps = CREATE_PATIENT_STEPS;
  const step = steps[Math.min(active, steps.length - 1)];
  const isLastStep = active >= steps.length - 1;
  const opensCpe = createsEnrollmentTask(config, isRemote);
  const askWho = asksWhoMonitors(config, isRemote);

  useEffect(() => {
    void trigger('mrn');
  }, [
    clinic?.ehrIntegration,
    clinic?.id,
    clinic?.parent?.ehrIntegration,
    clinicId,
    trigger,
    userSettings?.clinics
  ]);

  // User settings can arrive after the form mounts.
  useEffect(() => {
    if (!clinicId && defaultClinicId) setValue('clinicId', defaultClinicId);
  }, [clinicId, defaultClinicId, setValue]);

  // Rhythm is preselected when the question shows; changing the answer or clinic can drop it.
  useEffect(() => {
    if (askWho && !monitoredBy) setValue('monitoredBy', 'rhythm', { shouldValidate: true });
    if (!askWho && monitoredBy) setValue('monitoredBy', '');
  }, [askWho, monitoredBy, setValue]);

  const resetAll = () => {
    reset({ ...EMPTY_VALUES, clinicId: defaultClinicId });
    setActive(0);
    setUseFilter(true);
  };

  const clearError = () => {
    setIsError(false);
    setErrorType(false);
    setErrorPatientId('');
  };

  const goNext = async () => {
    const ok = await trigger(STEP_FIELDS[step]);
    if (ok) setActive((current) => Math.min(current + 1, steps.length - 1));
  };

  /**
   * The backend should open the CPE (and set the patient to Candidate) on create. Until it
   * returns `enrollmentTaskId`, internal users open it here; external users can't list task types.
   */
  const openEnrollmentTask = async (patientId: string, values: CreatePatientFormValues) => {
    const taskTypeId = taskTypes.find(
      (type) => type.code === CPE_TASK_TYPE_CODE && type.isActive
    )?.id;
    if (!taskTypeId) return null;
    const task = await createTask({
      taskTypeId,
      subject: 'Candidate Patient Enrollment',
      description: `New remote monitored patient ${values.firstName} ${values.lastName}, added from the patient list.`,
      organizationId: values.clinicId,
      patientId
    }).unwrap();
    return task.id;
  };

  const onSubmit = async (values: CreatePatientFormValues) => {
    setIsLoading(true);
    const remote = values.monitoring === 'remote';

    const payload: CreatePatientDto = {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: dayjs(values.dob).format('YYYY-MM-DDT00:00:00'),
      sex: values.sex as PatientSex,
      internalMRN: values.mrn.trim(),
      clinicId: values.clinicId,
      isRemoteMonitored: remote,
      isRhythmMonitored: rhythmMonitored(config, {
        isRemoteMonitored: remote,
        monitoredBy: values.monitoredBy || null,
        deviceType: values.type
      }),
      device: {
        deviceVendor: values.vendor as DeviceVendors,
        deviceType: values.type,
        modelName: values.modelName ?? '',
        modelNumber: values.modelNumber ?? '',
        serialNumber: values.serialNumber,
        implantDate: dayjs(values.implantDate).format('YYYY-MM-DDT00:00:00')
      }
    };

    try {
      const { data } = await createPatient(payload).unwrap();

      if (!data.success) {
        setIsError(true);
        setErrorMessage(data.message ?? '');
        setErrorType(
          data.message.includes('Patient already exists')
            ? 'Patient'
            : data.message.includes('Device already exists')
              ? 'Device'
              : false
        );
        setErrorPatientId(data.patient?.id ?? '');
        setIsLoading(false);
        return;
      }

      const patientId = data.patient?.id ?? '';
      let taskId: string | null = data.enrollmentTaskId ?? null;
      if (createsEnrollmentTask(config, remote) && !taskId) {
        try {
          taskId = await openEnrollmentTask(patientId, values);
        } catch (err) {
          console.log({ err });
        }
        if (!taskId) {
          notifications.show({
            title: 'Patient saved',
            message: 'The enrollment task could not be created. Ask the Rhythm team to open it.',
            color: 'yellow',
            position: 'bottom-right'
          });
        }
      }

      resetAll();
      onClose();
      clearError();
      navigate(
        taskId ? routes.physician.taskDetail(taskId) : routes.physician.patientDetail(patientId)
      );
      setIsLoading(false);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create patient.',
        color: 'red',
        position: 'bottom-right'
      });
      setIsLoading(false);
      console.log({ err });
    }
  };

  const close = () => {
    if (isError) {
      clearError();
    } else {
      resetAll();
      onClose();
    }
  };

  const monitoringChoices = isInternalUser
    ? [
        { value: 'remote' as const, label: 'Yes', description: 'Remote monitored' },
        { value: 'in_clinic' as const, label: 'No', description: 'Seen in clinic only' }
      ]
    : [
        {
          value: 'remote' as const,
          label: 'Remote monitored',
          description: 'Transmissions come in from the device'
        },
        {
          value: 'in_clinic' as const,
          label: 'In clinic only',
          description: 'Device checks happen in the clinic'
        }
      ];

  const monitorChoices = [
    { value: 'rhythm' as const, label: 'Rhythm', description: 'Rhythm monitors this patient' },
    {
      value: 'clinic' as const,
      label: isInternalUser ? 'The clinic' : 'Our clinic',
      description: 'The clinic monitors; Rhythm provides the platform'
    }
  ];

  return (
    <CustomModal
      className={classNames(poc.pocScope, poc.pocModal)}
      modalProps={{
        opened: isOpen,
        onClose: close,
        size: 'xl',
        centered: true,
        closeOnClickOutside: false,
        closeOnEscape: true
      }}
      header={{ title: isError ? 'Unable to Create Patient' : 'Add Patient' }}
      secondaryButton={{
        text: isError ? 'Back' : active > 0 ? 'Back' : 'Cancel',
        onClick: () => {
          if (isError) clearError();
          else if (active > 0) setActive((current) => current - 1);
          else close();
        }
      }}
      primaryButton={{
        text: isError
          ? 'Close'
          : isLastStep
            ? opensCpe
              ? 'Create and open enrollment'
              : 'Create'
            : 'Next',
        onClick: () => {
          if (isError) {
            clearError();
            resetAll();
            onClose();
          } else if (isLastStep) {
            void trigger(STEP_FIELDS[step]).then((ok) => {
              if (ok) void handleSubmit(onSubmit)();
            });
          } else {
            void goNext();
          }
        },
        isDisabled: isLoading || (step === 'monitoring' && clinicLoading),
        isLoading: isLoading
      }}>
      {isError ? (
        <div className={styles.errorContainer}>
          <div className={styles.errorIconRow}>
            <YellowAlertIcon size={28} />
            <Text className={styles.errorTitle}>
              {errorType === 'Patient'
                ? 'Patient Already Exists'
                : errorType === 'Device'
                  ? 'Device Already Exists'
                  : 'Unable to Create Patient'}
            </Text>
          </div>

          <Stack gap="xs">
            <Text size="md" className={styles.errorDescription}>
              {errorType === 'Patient'
                ? 'A patient with the same name, date of birth, or MRN already exists in the system. A duplicate record cannot be created.'
                : errorType === 'Device'
                  ? 'A device with the same manufacturer and serial number already exists in the system.'
                  : errorMessage || 'The patient could not be created.'}
            </Text>

            {errorPatientId && (
              <Anchor
                component="button"
                size="sm"
                onClick={() => {
                  onClose();
                  clearError();
                  navigate(routes.physician.patientDetail(errorPatientId));
                }}>
                View existing patient profile →
              </Anchor>
            )}

            <Text size="sm" c="dimmed" mt="xs">
              If you believe this is an error or need further assistance, please contact Rhythm
              Support.
            </Text>
          </Stack>
        </div>
      ) : (
        <div className={styles.reportForm}>
          <StepIndicator steps={steps.map((key) => STEP_LABEL[key])} active={active} />

          {step === 'monitoring' && (
            <Stack gap="lg">
              <Controller
                control={control}
                name="clinicId"
                render={({ field, fieldState }) => (
                  <ClinicSelect
                    clinics={scope.all}
                    filteredClinics={scope.filtered}
                    useFilter={useFilter}
                    onUseFilterChange={setUseFilter}
                    value={field.value}
                    onChange={(id) => field.onChange(id)}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <div>
                <Text className={styles.question}>
                  {isInternalUser
                    ? 'Will this patient be remote monitored?'
                    : 'Will this patient be remote monitored or seen in clinic only?'}
                  <span className={styles.required}>*</span>
                </Text>
                <Controller
                  control={control}
                  name="monitoring"
                  render={({ field, fieldState }) => (
                    <ChoiceCards
                      choices={monitoringChoices}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>

              {askWho && (
                <div>
                  <Text className={styles.question}>
                    Who will monitor this patient?
                    <span className={styles.required}>*</span>
                  </Text>
                  <Controller
                    control={control}
                    name="monitoredBy"
                    render={({ field, fieldState }) => (
                      <ChoiceCards
                        choices={monitorChoices}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
              )}

              {opensCpe && (
                <div className={styles.note}>
                  <IconInfoCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    This clinic uses Patient Enrollment. Saving creates a Candidate Patient
                    Enrollment task for the Rhythm team.
                  </span>
                </div>
              )}
            </Stack>
          )}

          {step === 'patient' && (
            <PatientFields
              control={control}
              mrnRequired={clinicRequiresInternalMrn(clinic)}
              mrnLabel={getMrnLabel(clinic?.useMpiLabel)}
            />
          )}

          {step === 'device' && (
            <Stack gap="md">
              <DeviceFields control={control} vendor={vendor} />
              {isRemote && !config.platformOnly && deviceType && config.deviceTypesMonitored && (
                <div className={styles.note}>
                  <IconInfoCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Rhythm monitored:{' '}
                    {rhythmMonitored(config, { isRemoteMonitored: true, deviceType })
                      ? 'Yes'
                      : 'No'}{' '}
                    (from the clinic&apos;s device types monitored)
                  </span>
                </div>
              )}
            </Stack>
          )}
        </div>
      )}
    </CustomModal>
  );
};

export default CreatePatientModal;
